import {
  type PostNordApiResponse,
  type PostNordDeliveryOptionsRequest,
  type PostNordDeliveryOptionsResponse,
  type PostNordEdiInstructionLabelsResponse,
  type PostNordEdiInstructionRequest,
  type PostNordEdiInstructionResponse,
  type PostNordEdiLabelsPdfParams,
  type PostNordErrorResponse,
  type PostNordPickupBookingRequest,
  type PostNordPickupBookingResponse,
  type PostNordValidatePostalCodeRequest,
  type PostNordValidatePostalCodeResponse
} from 'ecosystem/postnord'
import { createUrl, resolveUrlPath } from 'shared-utils'
import { apiCall, type ApiCallResponse } from '../fetching'
import { POSTNORD_API_KEY, POSTNORD_API_URL } from './constants'

const headersJson = {
  'Content-Type': 'application/json'
}

const postnordApiCall = async <Data>(
  url: string,
  {
    params,
    ...props
  }: RequestInit & {
    params?: Record<
      string,
      | undefined
      | null
      | string
      | number
      | boolean
      | (string | number | boolean | null | undefined)[]
    >
  }
): Promise<ApiCallResponse<Data>> => {
  if (!POSTNORD_API_KEY) {
    throw new Error('POSTNORD_API_KEY is not provided')
  }

  if (!POSTNORD_API_URL) {
    throw new Error('POSTNORD_API_URL is not provided')
  }

  const finalUrl = resolveUrlPath(
    POSTNORD_API_URL,
    createUrl(url, { apikey: POSTNORD_API_KEY, ...params })
  )

  const { error, data, response } = await apiCall<PostNordApiResponse<Data>>(finalUrl, props)

  // console.log('postnord api ', finalUrl, props, error, JSON.stringify(data, null, 2));

  if (error || !response) {
    return {
      data: null,
      error,
      response
    }
  }

  if (response.ok) {
    return {
      data: data as Data,
      error: null,
      response
    }
  }

  if (!data) {
    return {
      data: null,
      error: 'Unknown error',
      response
    }
  }

  if ('message' in (data as PostNordErrorResponse)) {
    return {
      data: null,
      error: (data as PostNordErrorResponse).message,
      response
    }
  }

  return {
    data: data as Data,
    error: null,
    response
  }
}

/**
 * This API can be used for:
 *
 * Validate if a postalcode is valid and exist in the Nordics.
 * Validate that a postalcode follows a valid pattern for non Nordic countries.
 * Check the correct pattern based on a country code.
 * @remarks Recommended to be used before sending the EDI- information to our Booking API.
 */
const postalCodeValidation = (payload: PostNordValidatePostalCodeRequest) =>
  postnordApiCall<PostNordValidatePostalCodeResponse>('/rest/shipment/v1/validate/postalcode', {
    method: 'POST',
    headers: headersJson,
    body: JSON.stringify(payload)
  })

/**
 * The Delivery Options (DO) API provides delivery options given an origin and a destination.
 *
 * Each delivery option provided by DO will contain information needed to select the best option
 * matching both sending and receiving parties' preferences.
 * This information includes:
 * When the recipient will receive the parcel.
 * Where the parcel will be delivered to.
 * Whether the delivery is fossil free, according to Swedish Industry Agreement, and/or Swan
 * ecolabelled.
 * Recommended texts which describes the delivery from a recipient's perspective (can be used
 * in a checkout)
 *
 * @remarks Recommended to be used by directly integrating customers, TA-systems and integration
 * partners.
 *
 * Visit our demo showroom at https://showroom.prod.eta.aws.pncloud.se to see how the DO API
 * enhances the e-commerce checkout freight picker.
 * For developers, read the detailed DO API specification at:
 * https://developer.postnord.com/apis/details?systemName=shipment-v1-deliveryoptions
 */
const getDeliveryOptions = (
  payload: PostNordDeliveryOptionsRequest,
  /**
   * Language of response data. Supported values: 'sv', 'en'.
   * @remarks The format is specified in RFC 4647.
   */
  language?: string
) =>
  postnordApiCall<PostNordDeliveryOptionsResponse>(
    '/rest/shipment/v1/deliveryoptions/bywarehouse',
    {
      method: 'POST',
      headers: {
        ...headersJson,
        'Accept-Language': language ?? 'sv'
      },
      body: JSON.stringify(payload)
    }
  )

/**
 * Book pickup parcel from the merchant's shop or warehouse
 */
const bookPickup = (payload: PostNordPickupBookingRequest) =>
  postnordApiCall<PostNordPickupBookingResponse>('/rest/shipment/v3/pickups', {
    method: 'POST',
    headers: headersJson,
    body: JSON.stringify(payload)
  })

/**
 * Book shipment based on the EDI instruction. Only EDI-information, no label will be created.
 */
const bookShipmentEdi = (payload: PostNordEdiInstructionRequest) =>
  postnordApiCall<PostNordEdiInstructionResponse>('/rest/shipment/v3/edi', {
    method: 'POST',
    headers: headersJson,
    body: JSON.stringify(payload)
  })

/**
 * Book shipment based on the EDI instruction and generate the referring PDF labels.
 */
const bookShipmentEdiLabelsPdf = (
  payload: PostNordEdiInstructionRequest,
  params?: PostNordEdiLabelsPdfParams
) => {
  return postnordApiCall<PostNordEdiInstructionLabelsResponse>('/rest/shipment/v3/edi/labels/pdf', {
    method: 'POST',
    headers: headersJson,
    params: {
      processOffline: true,
      storeLabel: true,
      ...params
    },
    body: JSON.stringify(payload)
  })
}

export const postnordApi = {
  postalCodeValidation,
  getDeliveryOptions,
  bookPickup,
  bookShipmentEdi,
  bookShipmentEdiLabelsPdf
}
